import { useState } from 'react';
import {
  FileText,
  Edit2,
  Save,
  X,
  Search,
  Globe,
  Home,
  Info,
  Briefcase,
  Phone,
  History,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface ContentBlock {
  id: string;
  key: string;
  title: string;
  content: string;
  page: string;
  type: 'text' | 'html' | 'markdown';
  lastUpdated: Date;
  updatedBy: string;
}

// Demo data - site içerikleri
const initialContent: ContentBlock[] = [
  {
    id: '1',
    key: 'hero_title',
    title: 'Ana Sayfa - Hero Basligi',
    content: 'Dijital Donusumunuzu Gerceklestirin',
    page: 'Anasayfa',
    type: 'text',
    lastUpdated: new Date('2024-01-15'),
    updatedBy: 'Admin',
  },
  {
    id: '2',
    key: 'hero_subtitle',
    title: 'Ana Sayfa - Hero Alt Basligi',
    content: 'Yenilikci yazilim cozumleri ile isletmenizi gelecege tasiyin',
    page: 'Anasayfa',
    type: 'text',
    lastUpdated: new Date('2024-01-15'),
    updatedBy: 'Admin',
  },
  {
    id: '3',
    key: 'about_description',
    title: 'Hakkimizda - Aciklama',
    content: 'Bosphorus Labs, 2020 yilindan bu yana Turkiye merkezli olarak yazilim gelistirme, yapay zeka ve bulut cozumleri alaninda hizmet vermektedir.',
    page: 'Hakkimizda',
    type: 'markdown',
    lastUpdated: new Date('2024-01-10'),
    updatedBy: 'Admin',
  },
  {
    id: '4',
    key: 'services_intro',
    title: 'Hizmetler - Giris Metni',
    content: 'Kapsamli yazilim cozumlerimiz ile isletmenizin dijital ihtiyaclarini karsilayin.',
    page: 'Hizmetler',
    type: 'text',
    lastUpdated: new Date('2024-01-12'),
    updatedBy: 'Admin',
  },
  {
    id: '5',
    key: 'contact_address',
    title: 'Iletisim - Adres',
    content: 'Levent, Istanbul, Turkiye',
    page: 'Iletisim',
    type: 'text',
    lastUpdated: new Date('2024-01-08'),
    updatedBy: 'Admin',
  },
  {
    id: '6',
    key: 'footer_copyright',
    title: 'Footer - Telif Hakki',
    content: '2024 Bosphorus Labs. Tum haklari saklidir.',
    page: 'Genel',
    type: 'text',
    lastUpdated: new Date('2024-01-01'),
    updatedBy: 'Admin',
  },
  {
    id: '7',
    key: 'cta_button_text',
    title: 'CTA - Buton Metni',
    content: 'Ucretsiz Demo Talep Et',
    page: 'Genel',
    type: 'text',
    lastUpdated: new Date('2024-01-05'),
    updatedBy: 'Admin',
  },
];

const pages = ['Tumu', 'Anasayfa', 'Hakkimizda', 'Hizmetler', 'Urunler', 'Iletisim', 'Genel'];

const pageIcons: Record<string, React.ReactNode> = {
  'Anasayfa': <Home size={16} />,
  'Hakkimizda': <Info size={16} />,
  'Hizmetler': <Briefcase size={16} />,
  'Urunler': <FileText size={16} />,
  'Iletisim': <Phone size={16} />,
  'Genel': <Globe size={16} />,
};

export function AdminContent() {
  const [content, setContent] = useState<ContentBlock[]>(initialContent);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState('Tumu');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Filter content
  const filteredContent = content.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPage = selectedPage === 'Tumu' || item.page === selectedPage;
    return matchesSearch && matchesPage;
  });

  // Group by page
  const groupedContent = filteredContent.reduce((acc, item) => {
    if (!acc[item.page]) {
      acc[item.page] = [];
    }
    acc[item.page].push(item);
    return acc;
  }, {} as Record<string, ContentBlock[]>);

  const handleEdit = (item: ContentBlock) => {
    setEditingId(item.id);
    setEditValue(item.content);
  };

  const handleSave = (id: string) => {
    setContent(content.map(item =>
      item.id === id
        ? { ...item, content: editValue, lastUpdated: new Date(), updatedBy: 'Admin' }
        : item
    ));
    setEditingId(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Icerik Yonetimi</h1>
          <p className="text-text-secondary">Site iceriklerini ve metinlerini duzenleyin</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Icerik ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
      </div>

      {/* Page Tabs */}
      <div className="flex flex-wrap gap-2">
        {pages.map(page => (
          <button
            key={page}
            onClick={() => setSelectedPage(page)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              selectedPage === page
                ? 'bg-accent text-white'
                : 'bg-bg-card border border-border-default text-text-secondary hover:text-text-primary hover:border-accent/50'
            )}
          >
            {page !== 'Tumu' && pageIcons[page]}
            {page}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Icerik', value: content.length },
          { label: 'Sayfa Sayisi', value: new Set(content.map(c => c.page)).size },
          { label: 'Son Guncelleme', value: 'Bugun' },
          { label: 'Aktif Dil', value: 'TR' },
        ].map((stat, index) => (
          <div key={index} className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-accent">{stat.value}</p>
            <p className="text-sm text-text-secondary">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Content List */}
      <div className="space-y-6">
        {Object.entries(groupedContent).map(([page, items]) => (
          <div key={page} className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 bg-bg-elevated border-b border-border-default">
              {pageIcons[page] || <FileText size={16} />}
              <h2 className="font-semibold text-text-primary">{page}</h2>
              <span className="text-xs text-text-tertiary">({items.length} icerik)</span>
            </div>
            <div className="divide-y divide-border-default">
              {items.map((item) => (
                <div key={item.id} className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-text-primary">{item.title}</h3>
                        <code className="px-2 py-0.5 rounded bg-bg-elevated text-xs text-text-tertiary">
                          {item.key}
                        </code>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-text-tertiary">
                        <span className="flex items-center gap-1">
                          <History size={12} />
                          {item.lastUpdated.toLocaleDateString('tr-TR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {item.updatedBy}
                        </span>
                        <span className={cn(
                          'px-2 py-0.5 rounded-full text-xs',
                          item.type === 'text' ? 'bg-blue-500/10 text-blue-500' :
                          item.type === 'html' ? 'bg-orange-500/10 text-orange-500' :
                          'bg-purple-500/10 text-purple-500'
                        )}>
                          {item.type.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    {editingId !== item.id && (
                      <button
                        onClick={() => handleEdit(item)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-accent hover:bg-accent/10 transition-colors"
                      >
                        <Edit2 size={14} />
                        Duzenle
                      </button>
                    )}
                  </div>

                  {editingId === item.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={item.type === 'markdown' ? 6 : 3}
                        className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-accent focus:outline-none resize-none font-mono text-sm"
                        autoFocus
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={handleCancel}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary transition-colors"
                        >
                          <X size={16} />
                          Iptal
                        </button>
                        <button
                          onClick={() => handleSave(item.id)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
                        >
                          <Save size={16} />
                          Kaydet
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-bg-elevated">
                      <p className="text-text-primary whitespace-pre-wrap">{item.content}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {filteredContent.length === 0 && (
        <div className="text-center py-12 bg-bg-card border border-border-default rounded-xl">
          <FileText className="mx-auto mb-4 text-text-tertiary" size={48} />
          <p className="text-text-tertiary">Icerik bulunamadi</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="text-blue-500 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-500 mb-1">Icerik Yonetimi Hakkinda</p>
            <p className="text-sm text-blue-500/80">
              Burada yaptiginiz degisiklikler aninda siteye yansir. Dikkatli olun ve degisiklikleri kaydetmeden once kontrol edin.
              Markdown formatinda icerikler icin ozel formatlama kullanabilirsiniz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
